<p-toast></p-toast>
<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<app-page-shell *ngIf="clearance">
    <app-page-detail-hero [auditStamp]="{ createdAt: clearance.createdDate, updatedAt: clearance.updatedDate, createdByActor: clearance.createdByActor }"
        eyebrow="Clearance Workspace"
        icon="pi pi-briefcase"
        [title]="clearance.jobNumber || clearance.clearanceJobNumber || 'Clearance'"
        [subtitle]="(clearance.consignee?.name || 'No consignee') + ' | ' + (clearance.broker?.name || 'No broker') + ' | ETA ' + ((clearance.eta | date:'mediumDate') || '-')">
        <div tags>
            <p-tag [value]="clearance.status" [severity]="getStatusSeverity(clearance.status)"></p-tag>
            <p-tag [value]="readiness?.readinessStatus || 'Checking'" [severity]="getReadinessSeverity()"></p-tag>
            <p-tag [value]="clearance.bayanNumber || 'No Bayan'" severity="info"></p-tag>
            <p-tag *ngIf="hasLogisticsProject()" [value]="'Project #' + clearance.logisticsProjectId" severity="contrast"></p-tag>
        </div>
        <div actions>
            <app-execution-sales-order-button sourceType="CUSTOMS_CLEARANCE" [sourceId]="clearance.clearanceHeaderID"
                [owningCompanyId]="clearance.owningCompanyId" [sourceLabel]="clearance.jobNumber || clearance.clearanceJobNumber"></app-execution-sales-order-button>
            <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" routerLink="/clearance"></p-button>
            <p-button *ngIf="hasLogisticsProject()" label="Open Project" icon="pi pi-sitemap" severity="secondary" [outlined]="true"
                (click)="openProject()"></p-button>
            <p-button *ngIf="canEditClearance" label="Edit Header" icon="pi pi-pencil" severity="secondary" [outlined]="true"
                [routerLink]="['/clearance/edit', clearance.clearanceHeaderID]"></p-button>
            <p-button label="Refresh" icon="pi pi-refresh" severity="secondary" [outlined]="true" (click)="loadWorkspace()"></p-button>
        </div>
    </app-page-detail-hero>

    <app-page-metric-strip [metrics]="workspaceMetrics"></app-page-metric-strip>

    <app-page-data-panel title="Clearance Workspace" subtitle="Declarations, HS items, compliance documents, authorization, links, productivity, and workload.">
        <nav class="workspace-tabs" aria-label="Clearance workspace sections">
            <button type="button" *ngFor="let tab of workspaceTabs" class="workspace-tab-button"
                (click)="setWorkspaceTab(tab.id)" [class.active]="activeWorkspaceTab === tab.id">
                <span class="workspace-tab">
                    <i [class]="tab.icon"></i>
                    <span>{{ tab.label }}</span>
                </span>
            </button>
        </nav>
    </app-page-data-panel>

    <app-page-data-panel *ngIf="activeWorkspaceTab === 'overview'" title="Readiness Overview"
        subtitle="Local Phase 1 filing readiness checks.">
        <div class="grid">
            <div class="col-12 md:col-4">
                <div class="workspace-tile">
                    <span>Pre-clearance deadline</span>
                    <strong>{{ readiness?.preClearanceDeadline ? (readiness?.preClearanceDeadline | date:'medium') : '-' }}</strong>
                </div>
            </div>
            <div class="col-12 md:col-4">
                <div class="workspace-tile">
                    <span>Port / Customs location</span>
                    <strong>{{ selectedDeclaration?.customsPortName || selectedDeclaration?.customsPortCode || clearance.customsLocation || clearance.pod || '-' }}</strong>
                </div>
            </div>
            <div class="col-12 md:col-4">
                <div class="workspace-tile">
                    <span>Delivery link</span>
                    <strong>{{ clearance.shipment?.shipmentNumber || 'No shipment generated' }}</strong>
                </div>
            </div>
        </div>

        <div class="grid mt-2">
            <div class="col-12 md:col-6">
                <h3>Blockers</h3>
                <div *ngIf="!readiness?.blockers?.length" class="empty-note">No filing blockers detected.</div>
                <div *ngFor="let blocker of readiness?.blockers" class="issue-row danger">{{ blocker }}</div>
            </div>
            <div class="col-12 md:col-6">
                <h3>Warnings</h3>
                <div *ngIf="!readiness?.warnings?.length" class="empty-note">No active warnings.</div>
                <div *ngFor="let warning of readiness?.warnings" class="issue-row warn">{{ warning }}</div>
            </div>
        </div>

        <div class="grid mt-2">
            <div class="col-12 md:col-6">
                <h3>Completion Blockers</h3>
                <div *ngIf="!executionReadiness?.blockers?.length" class="empty-note">No completion blockers detected.</div>
                <div *ngFor="let blocker of executionReadiness?.blockers" class="issue-row danger">{{ blocker }}</div>
            </div>
            <div class="col-12 md:col-6">
                <h3>Outlay Position</h3>
                <div class="workspace-tile">
                    <span>Assessed / paid / outstanding</span>
                    <strong>{{ formatAmount(financialSummary?.assessedAmount) }} / {{ formatAmount(financialSummary?.paidAmount) }} / {{ formatAmount(financialSummary?.outstandingAmount) }}</strong>
                </div>
            </div>
        </div>
    </app-page-data-panel>

    <app-page-data-panel *ngIf="activeWorkspaceTab === 'declarations'" title="Declarations"
        subtitle="Manual Fasah/Bayan declaration records until live integrations are added.">
        <div actions>
            <p-button label="New Declaration" icon="pi pi-plus" (click)="newDeclaration()"></p-button>
        </div>

        <div class="workspace-grid">
            <div class="record-card-list" *ngIf="declarations.length; else noDeclarations">
                <article class="record-card selectable" *ngFor="let declaration of declarations"
                    [class.selected]="selectedDeclaration?.id === declaration.id" (click)="selectDeclaration(declaration)">
                    <div class="record-card-main">
                        <div class="record-card-title">
                            <span class="record-card-eyebrow">Declaration</span>
                            <strong>{{ declaration.declarationNo || declaration.declarationReferenceNo || 'Draft declaration' }}</strong>
                            <span>{{ declaration.bayanNumber || 'No Bayan number' }}</span>
                        </div>
                        <div class="record-card-actions">
                            <p-tag [value]="declaration.customsStatus || 'DRAFT'" severity="info"></p-tag>
                            <p-button icon="pi pi-pencil" label="Edit" severity="secondary" [outlined]="true"
                                (click)="$event.stopPropagation(); selectDeclaration(declaration); openEntryDialog('declaration')"></p-button>
                        </div>
                    </div>
                    <div class="record-card-fields">
                        <div><span>Transport mode</span><strong>{{ declaration.transportMode || '-' }}</strong></div>
                        <div><span>Port</span><strong>{{ declaration.customsPortName || declaration.customsPortCode || '-' }}</strong></div>
                        <div><span>Port type</span><strong>{{ declaration.portType || '-' }}</strong></div>
                        <div><span>Payment mode</span><strong>{{ declaration.paymentMode || '-' }}</strong></div>
                        <div><span>Fasah status</span><strong>{{ declaration.fasahStatus || '-' }}</strong></div>
                        <div><span>Inspection</span><strong>{{ declaration.inspectionStatus || '-' }}</strong></div>
                    </div>
                    <div class="record-card-note" *ngIf="declaration.remarks">{{ declaration.remarks }}</div>
                </article>
            </div>
            <ng-template #noDeclarations>
                <div class="empty-record-state">No declarations recorded.</div>
            </ng-template>

            <p-dialog styleClass="clearance-entry-dialog" [modal]="true" [draggable]="false" [resizable]="false"
                [visible]="activeEntryDialog === 'declaration'" (onHide)="closeEntryDialog()"
                (visibleChange)="onEntryDialogVisibleChange($event, 'declaration')"
                [header]="declarationDraft.id ? 'Edit Declaration' : 'Create Declaration'">
            <div class="workspace-form dialog-form clearance-dialog-form">
                <h3>{{ declarationDraft.id ? 'Edit Declaration' : 'Declaration Draft' }}</h3>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-file-edit"></i>
                        <div>
                            <h4>Declaration Identity</h4>
                            <p>References used by customs, Bayan/Fasah, and internal tracking.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-tag"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="declarationDraft.declarationReferenceNo" />
                        <label>Declaration reference</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-hashtag"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="declarationDraft.declarationNo" />
                        <label>Declaration number</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-shield"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="declarationDraft.bayanNumber" />
                        <label>Bayan number</label>
                    </p-iftalabel>
                </p-inputgroup>
                    </div>
                </section>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-map-marker"></i>
                        <div>
                            <h4>Movement And Port</h4>
                            <p>Transport mode and customs port details for filing route context.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-truck"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="declarationTransportMode" [options]="transportModeOptions"
                            [(ngModel)]="declarationDraft.transportMode" optionLabel="label" optionValue="value"
                            appendTo="body" styleClass="w-full"></p-select>
                        <label for="declarationTransportMode">Transport mode</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-map"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="declarationDraft.customsPortCode" />
                        <label>Customs port code</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-building"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="declarationDraft.customsPortName" />
                        <label>Customs port name</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-compass"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="declarationPortType" [options]="portTypeOptions"
                            [(ngModel)]="declarationDraft.portType" optionLabel="label" optionValue="value"
                            appendTo="body" styleClass="w-full"></p-select>
                        <label for="declarationPortType">Port type</label>
                    </p-iftalabel>
                </p-inputgroup>
                    </div>
                </section>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-check-circle"></i>
                        <div>
                            <h4>Status And Notes</h4>
                            <p>Payment mode, current customs state, and operational remarks.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-credit-card"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="declarationPaymentMode" [options]="customsPaymentModeOptions"
                            [(ngModel)]="declarationDraft.paymentMode" optionLabel="label" optionValue="value"
                            appendTo="body" styleClass="w-full"></p-select>
                        <label for="declarationPaymentMode">Payment mode</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-flag"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="declarationCustomsStatus" [options]="customsStatusOptions"
                            [(ngModel)]="declarationDraft.customsStatus" optionLabel="label" optionValue="value"
                            appendTo="body" styleClass="w-full"></p-select>
                        <label for="declarationCustomsStatus">Customs status</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup class="wide-field">
                    <p-inputgroup-addon><i class="pi pi-align-left"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <textarea pInputText [(ngModel)]="declarationDraft.remarks" rows="3"></textarea>
                        <label>Remarks</label>
                    </p-iftalabel>
                </p-inputgroup>
                    </div>
                </section>
                <div class="dialog-actions">
                    <p-button label="Save Declaration" icon="pi pi-save" (click)="saveDeclaration()"></p-button>
                </div>
            </div>
            </p-dialog>
        </div>
    </app-page-data-panel>

    <app-page-data-panel *ngIf="activeWorkspaceTab === 'items'" title="Declaration Items"
        [subtitle]="selectedDeclaration ? 'HS/tariff goods lines for selected declaration' : 'Select or save a declaration first'">
        <div actions>
            <p-button label="Add Item" icon="pi pi-plus" (click)="newItem()" [disabled]="!selectedDeclaration?.id"></p-button>
        </div>
        <div class="workspace-grid">
            <div class="record-card-list" *ngIf="declarationItems.length; else noDeclarationItems">
                <article class="record-card" *ngFor="let item of declarationItems">
                    <div class="record-card-main">
                        <div class="record-card-title">
                            <span class="record-card-eyebrow">HS item</span>
                            <strong>{{ item.hsCode || 'No HS code' }}</strong>
                            <span>{{ item.goodsDescription || item.tariffDescription || 'No goods description' }}</span>
                        </div>
                        <div class="record-card-actions">
                            <p-button icon="pi pi-pencil" label="Edit" severity="secondary" [outlined]="true" (click)="editItem(item)"></p-button>
                        </div>
                    </div>
                    <div class="record-card-fields">
                        <div><span>Origin</span><strong>{{ item.countryOfOriginCode || '-' }}</strong></div>
                        <div><span>Quantity</span><strong>{{ item.quantity || '-' }} {{ item.uomCode || '' }}</strong></div>
                        <div><span>Customs value</span><strong>{{ item.customsValue || '-' }} {{ item.currencyCode || '' }}</strong></div>
                        <div><span>Tariff description</span><strong>{{ item.tariffDescription || '-' }}</strong></div>
                        <div><span>Restricted</span><strong>{{ item.restrictedGoods ? 'Yes' : 'No' }}</strong></div>
                        <div><span>Permit / Saber</span><strong>{{ item.permitRequired ? 'Permit' : 'No permit' }} / {{ item.saberRequired ? 'Saber' : 'No Saber' }}</strong></div>
                    </div>
                </article>
            </div>
            <ng-template #noDeclarationItems>
                <div class="empty-record-state">No declaration items recorded for the selected declaration.</div>
            </ng-template>

            <p-dialog styleClass="clearance-entry-dialog" [modal]="true" [draggable]="false" [resizable]="false"
                [visible]="activeEntryDialog === 'item'" (onHide)="closeEntryDialog()"
                (visibleChange)="onEntryDialogVisibleChange($event, 'item')"
                [header]="itemDraft.id ? 'Edit Declaration Item' : 'Add Declaration Item'">
            <div class="workspace-form dialog-form clearance-dialog-form">
                <h3>{{ itemDraft.id ? 'Edit Item' : 'Add Item' }}</h3>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-list"></i>
                        <div>
                            <h4>Goods Classification</h4>
                            <p>HS code, description, and origin used for customs tariff handling.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-barcode"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="itemDraft.hsCode" />
                        <label>HS code</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-align-left"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="itemDraft.goodsDescription" />
                        <label>Goods description</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-flag"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="itemOriginCountry" [options]="countries"
                            [(ngModel)]="itemDraft.countryOfOriginCode" optionLabel="name" optionValue="countryCode"
                            [filter]="true" [showClear]="true" appendTo="body" styleClass="w-full"></p-select>
                        <label for="itemOriginCountry">Origin country</label>
                    </p-iftalabel>
                </p-inputgroup>
                    </div>
                </section>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-calculator"></i>
                        <div>
                            <h4>Quantity And Value</h4>
                            <p>Commercial quantity, UOM, customs value, and declaration currency.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-sort-numeric-up"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText type="number" [(ngModel)]="itemDraft.quantity" />
                        <label>Quantity</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-box"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="itemDraft.uomCode" />
                        <label>UOM</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-money-bill"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText type="number" [(ngModel)]="itemDraft.customsValue" />
                        <label>Customs value</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-wallet"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="itemCurrency" [options]="currencyOptions"
                            [(ngModel)]="itemDraft.currencyCode" optionLabel="label" optionValue="value"
                            [filter]="true" appendTo="body" styleClass="w-full"></p-select>
                        <label for="itemCurrency">Currency</label>
                    </p-iftalabel>
                </p-inputgroup>
                    </div>
                </section>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-shield"></i>
                        <div>
                            <h4>Regulatory Controls</h4>
                            <p>Flags that affect import restrictions, permits, and Saber requirements.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <div class="checkbox-row">
                    <label><input type="checkbox" [(ngModel)]="itemDraft.restrictedGoods" /> Restricted</label>
                    <label><input type="checkbox" [(ngModel)]="itemDraft.permitRequired" /> Permit</label>
                    <label><input type="checkbox" [(ngModel)]="itemDraft.saberRequired" /> Saber</label>
                </div>
                    </div>
                </section>
                <div class="dialog-actions">
                    <p-button label="Save Item" icon="pi pi-save" (click)="saveItem()"></p-button>
                </div>
            </div>
            </p-dialog>
        </div>
    </app-page-data-panel>

    <app-page-data-panel *ngIf="activeWorkspaceTab === 'documents'" title="Compliance Documents"
        subtitle="Required/optional checklist linked to uploaded clearance files.">
        <div actions class="document-actions">
            <p-button label="Generate Default Checklist" icon="pi pi-list-check" severity="secondary" [outlined]="true" (click)="generateDocuments()"></p-button>
            <p-button label="Add Document" icon="pi pi-plus" (click)="newDocument()"></p-button>
        </div>
        <div class="workspace-grid">
            <div class="document-card-grid" *ngIf="complianceDocuments.length; else noComplianceDocuments">
                <article class="document-card" *ngFor="let document of complianceDocuments">
                    <button type="button" class="document-delete-action" aria-label="Edit document" (click)="editDocument(document)">
                        <i class="pi pi-pencil"></i>
                    </button>

                    <div class="document-card-main">
                        <span class="document-card-icon">
                            <i class="pi pi-file"></i>
                        </span>
                        <div class="document-card-title">
                            <strong>{{ documentTypeLabel(document) }}</strong>
                            <span>{{ document.documentNumber || 'No document number' }}</span>
                        </div>
                    </div>

                    <div class="document-card-meta">
                        <span>{{ document.issuingAuthority || 'Issuing authority pending' }}</span>
                        <span>{{ document.updatedAt || document.createdAt ? ((document.updatedAt || document.createdAt) | date:'dd-MM-yyyy HH:mm') : 'No update date' }}</span>
                    </div>

                    <div class="document-card-file">
                        @if (documentFile(document); as uploadedFile) {
                            <a class="uploaded-file" [href]="uploadedFile.fileUrl" target="_blank" rel="noopener">
                                <i class="pi pi-paperclip"></i>
                                <span>{{ uploadedFile.fileName }}</span>
                            </a>
                            <span>{{ uploadedFile.fileSize ? ((uploadedFile.fileSize / 1024) | number:'1.0-0') + ' KB' : 'Size pending' }}</span>
                        } @else {
                            <span class="empty-file">
                                <i class="pi pi-exclamation-circle"></i>
                                No file uploaded
                            </span>
                            <span>{{ document.required ? 'Required' : 'Optional' }}</span>
                        }
                    </div>

                    <div class="document-card-footer">
                        <p-tag [value]="documentPresenceStatus(document)" [severity]="getDocumentSeverity(documentPresenceStatus(document))"></p-tag>
                        <span>{{ document.required ? 'Required document' : 'Optional document' }}</span>
                    </div>
                </article>
            </div>

            <ng-template #noComplianceDocuments>
                <div class="empty-document-state">
                    <i class="pi pi-folder-open"></i>
                    <strong>No compliance documents yet.</strong>
                    <span>Generate the default checklist or add a document manually.</span>
                </div>
            </ng-template>

            <p-dialog styleClass="clearance-entry-dialog" [modal]="true" [draggable]="false" [resizable]="false"
                [visible]="activeEntryDialog === 'document'" (onHide)="closeEntryDialog()"
                (visibleChange)="onEntryDialogVisibleChange($event, 'document')"
                [header]="documentDraft.id ? 'Edit Compliance Document' : 'Add Compliance Document'">
            <div class="workspace-form dialog-form clearance-dialog-form">
                <h3>{{ documentDraft.id ? 'Edit Document' : 'Add Checklist Item' }}</h3>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-file"></i>
                        <div>
                            <h4>Document Details</h4>
                            <p>Document type, reference, and issuing party for the clearance checklist.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-file"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="complianceDocumentType" [options]="customsClearanceFileTypes"
                            [(ngModel)]="documentDraft.documentType" optionLabel="label" optionValue="value"
                            placeholder="Select document type" [filter]="true" appendTo="body" styleClass="w-full"></p-select>
                        <label for="complianceDocumentType">Document type</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-hashtag"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="documentDraft.documentNumber" />
                        <label>Document number</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-building"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="documentDraft.issuingAuthority" />
                        <label>Issuing authority</label>
                    </p-iftalabel>
                </p-inputgroup>
                    </div>
                </section>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-cloud-upload"></i>
                        <div>
                            <h4>Upload Evidence</h4>
                            <p>Attach the Firebase-backed file; status is derived from whether a file exists.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <div class="document-upload-card wide-field">
                    <div class="document-upload-header">
                        <div>
                            <span>Document Evidence</span>
                            <strong>{{ documentFileTypeLabel() }}</strong>
                        </div>
                        <p-tag [value]="documentDraft.fileId ? 'UPLOADED' : 'MISSING'"
                            [severity]="documentDraft.fileId ? 'success' : 'warn'"></p-tag>
                    </div>

                    <p-table [value]="[documentDraft]" styleClass="p-datatable-sm document-table">
                        <ng-template pTemplate="header">
                            <tr>
                                <th>Document Type</th>
                                <th>Current File</th>
                                <th>Upload</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body">
                            <tr>
                                <td>
                                    <span class="doc-type">
                                        <i class="pi pi-file"></i>
                                        {{ documentDraft.documentType || documentFileTypeLabel() }}
                                    </span>
                                </td>
                                <td>
                                    @if (documentFile(); as uploadedFile) {
                                        <a class="uploaded-file" [href]="uploadedFile.fileUrl" target="_blank" rel="noopener">
                                            {{ uploadedFile.fileName }}
                                        </a>
                                        <p-button type="button" icon="pi pi-times" [text]="true" severity="secondary"
                                            (click)="removeDocumentFile()"></p-button>
                                    } @else {
                                        <span class="empty-file">No file</span>
                                    }
                                    @if (documentUploadProgress > 0) {
                                        <p-progressBar [value]="documentUploadProgress"></p-progressBar>
                                    }
                                </td>
                                <td>
                                    <p-fileUpload name="clearanceDocument" mode="basic" [customUpload]="true"
                                        [disabled]="documentUploadProgress > 0 || !canUploadFile"
                                        (onSelect)="onDocumentFileUpload($event)"
                                        [chooseLabel]="documentDraft.fileId ? 'Replace' : 'Upload'"
                                        [chooseIcon]="documentDraft.fileId ? 'pi pi-refresh' : 'pi pi-cloud-upload'"></p-fileUpload>
                                </td>
                            </tr>
                        </ng-template>
                    </p-table>
                </div>
                    </div>
                </section>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-info-circle"></i>
                        <div>
                            <h4>Review Notes</h4>
                            <p>Required flag and operational comments for missing or rejected documents.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup class="wide-field">
                    <p-inputgroup-addon><i class="pi pi-align-left"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <textarea pInputText [(ngModel)]="documentDraft.rejectionReason" rows="3"></textarea>
                        <label>Notes / rejection reason</label>
                    </p-iftalabel>
                </p-inputgroup>
                <label><input type="checkbox" [(ngModel)]="documentDraft.required" /> Required</label>
                    </div>
                </section>
                <div class="dialog-actions">
                    <p-button label="Save Document" icon="pi pi-save" (click)="saveDocument()"></p-button>
                </div>
            </div>
            </p-dialog>
        </div>
    </app-page-data-panel>

    <app-page-data-panel *ngIf="activeWorkspaceTab === 'authorization'" title="Broker Authorization"
        subtitle="Importer and broker authorization evidence for customs filing.">
        <div actions>
            <p-button label="Add Authorization" icon="pi pi-plus" (click)="newAuthorization()"></p-button>
        </div>
        <div class="workspace-grid">
            <div class="record-card-list" *ngIf="brokerAuthorizations.length; else noAuthorizations">
                <article class="record-card" *ngFor="let authorization of brokerAuthorizations">
                    <div class="record-card-main">
                        <div class="record-card-title">
                            <span class="record-card-eyebrow">Broker authorization</span>
                            <strong>{{ authorization.brokerAuthorizationCode || authorization.brokerLicenseNo || 'Authorization draft' }}</strong>
                            <span>{{ authorization.brokerLicenseType || 'No license type' }}</span>
                        </div>
                        <div class="record-card-actions">
                            <p-tag [value]="authorization.status || 'ACTIVE'" severity="success"></p-tag>
                            <p-button icon="pi pi-pencil" label="Edit" severity="secondary" [outlined]="true" (click)="editAuthorization(authorization)"></p-button>
                        </div>
                    </div>
                    <div class="record-card-fields">
                        <div><span>License no</span><strong>{{ authorization.brokerLicenseNo || '-' }}</strong></div>
                        <div><span>Representative no</span><strong>{{ authorization.representativeNo || '-' }}</strong></div>
                        <div><span>Importer auth code</span><strong>{{ authorization.importerAuthorizationCode || '-' }}</strong></div>
                        <div><span>Customs port</span><strong>{{ authorization.customsPortCode || '-' }}</strong></div>
                        <div><span>Source</span><strong>{{ authorization.source || '-' }}</strong></div>
                        <div><span>Valid from</span><strong>{{ authorization.validFrom ? (authorization.validFrom | date:'mediumDate') : '-' }}</strong></div>
                        <div><span>Valid to</span><strong>{{ authorization.validTo ? (authorization.validTo | date:'mediumDate') : '-' }}</strong></div>
                    </div>
                </article>
            </div>
            <ng-template #noAuthorizations>
                <div class="empty-record-state">No broker authorization records.</div>
            </ng-template>

            <p-dialog styleClass="clearance-entry-dialog" [modal]="true" [draggable]="false" [resizable]="false"
                [visible]="activeEntryDialog === 'authorization'" (onHide)="closeEntryDialog()"
                (visibleChange)="onEntryDialogVisibleChange($event, 'authorization')"
                [header]="authorizationDraft.id ? 'Edit Broker Authorization' : 'Add Broker Authorization'">
            <div class="workspace-form dialog-form clearance-dialog-form">
                <h3>{{ authorizationDraft.id ? 'Edit Authorization' : 'Add Authorization' }}</h3>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-id-card"></i>
                        <div>
                            <h4>Broker License</h4>
                            <p>Broker license and representative details used for customs filing authorization.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-id-card"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="brokerLicenseType" [options]="brokerLicenseTypeOptions"
                            [(ngModel)]="authorizationDraft.brokerLicenseType" optionLabel="label" optionValue="value"
                            appendTo="body" styleClass="w-full"></p-select>
                        <label for="brokerLicenseType">Broker license type</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-hashtag"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="authorizationDraft.brokerLicenseNo" />
                        <label>Broker license no</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-user"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="authorizationDraft.representativeNo" />
                        <label>Representative no</label>
                    </p-iftalabel>
                </p-inputgroup>
                    </div>
                </section>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-shield"></i>
                        <div>
                            <h4>Authorization References</h4>
                            <p>Broker/importer authorization codes and port scope.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-key"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="authorizationDraft.brokerAuthorizationCode" />
                        <label>Broker authorization code</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-lock"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="authorizationDraft.importerAuthorizationCode" />
                        <label>Importer authorization code</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-map-marker"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="authorizationDraft.customsPortCode" />
                        <label>Customs port code</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-check-circle"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="brokerAuthorizationStatus" [options]="brokerAuthorizationStatusOptions"
                            [(ngModel)]="authorizationDraft.status" optionLabel="label" optionValue="value"
                            appendTo="body" styleClass="w-full"></p-select>
                        <label for="brokerAuthorizationStatus">Status</label>
                    </p-iftalabel>
                </p-inputgroup>
                    </div>
                </section>
                <div class="dialog-actions">
                    <p-button label="Save Authorization" icon="pi pi-save" (click)="saveAuthorization()"></p-button>
                </div>
            </div>
            </p-dialog>
        </div>
    </app-page-data-panel>

    <app-page-data-panel *ngIf="activeWorkspaceTab === 'duties'" title="Duties & Payments"
        subtitle="Operational ledger for customs duty, import VAT, port outlays, and payment evidence.">
        <div actions>
            <p-button label="New Assessment" icon="pi pi-plus" (click)="newAssessment()" [disabled]="!selectedDeclaration?.id"></p-button>
            <p-button label="New Receipt" icon="pi pi-receipt" severity="secondary" [outlined]="true" (click)="newPaymentReceipt()"></p-button>
        </div>
        <div class="grid">
            <div class="col-12 md:col-3">
                <div class="workspace-tile">
                    <span>Customs value</span>
                    <strong>{{ formatAmount(financialSummary?.totalCustomsValue) }}</strong>
                </div>
            </div>
            <div class="col-12 md:col-3">
                <div class="workspace-tile">
                    <span>Duty</span>
                    <strong>{{ formatAmount(financialSummary?.dutyAmount) }}</strong>
                </div>
            </div>
            <div class="col-12 md:col-3">
                <div class="workspace-tile">
                    <span>Import VAT</span>
                    <strong>{{ formatAmount(financialSummary?.importVatAmount) }}</strong>
                </div>
            </div>
            <div class="col-12 md:col-3">
                <div class="workspace-tile">
                    <span>Outstanding</span>
                    <strong>{{ formatAmount(financialSummary?.outstandingAmount) }}</strong>
                </div>
            </div>
        </div>

        <div class="workspace-grid mt-3">
            <div>
                <h3>Assessments</h3>
                <div class="record-card-list" *ngIf="dutyTaxAssessments.length; else noAssessments">
                    <article class="record-card finance" *ngFor="let assessment of dutyTaxAssessments">
                        <div class="record-card-main">
                            <div class="record-card-title">
                                <span class="record-card-eyebrow">Duty assessment</span>
                                <strong>{{ assessment.assessmentReferenceNo || 'No assessment reference' }}</strong>
                                <span>{{ assessment.paymentMode || 'No payment mode' }}</span>
                            </div>
                            <div class="record-card-actions">
                                <p-tag [value]="assessment.assessmentStatus || 'ASSESSED'" [severity]="getAssessmentSeverity(assessment.assessmentStatus)"></p-tag>
                                <p-button icon="pi pi-pencil" label="Edit" severity="secondary" [outlined]="true" (click)="editAssessment(assessment)"></p-button>
                            </div>
                        </div>
                        <div class="record-card-fields">
                            <div><span>Customs value</span><strong>{{ formatAmount(assessment.totalCustomsValue, assessment.currencyCode || 'SAR') }}</strong></div>
                            <div><span>Duty</span><strong>{{ formatAmount(assessment.dutyAmount, assessment.currencyCode || 'SAR') }}</strong></div>
                            <div><span>Import VAT</span><strong>{{ formatAmount(assessment.importVatAmount, assessment.currencyCode || 'SAR') }}</strong></div>
                            <div><span>Other fees</span><strong>{{ formatAmount(assessment.otherFeesAmount, assessment.currencyCode || 'SAR') }}</strong></div>
                            <div><span>Total</span><strong>{{ formatAmount(assessment.totalAssessmentAmount, assessment.currencyCode || 'SAR') }}</strong></div>
                            <div><span>Payment ref</span><strong>{{ assessment.paymentReference || '-' }}</strong></div>
                        </div>
                        <div class="record-card-note" *ngIf="assessment.remarks">{{ assessment.remarks }}</div>
                    </article>
                </div>
                <ng-template #noAssessments>
                    <div class="empty-record-state">No duty/tax assessments recorded for the selected declaration.</div>
                </ng-template>

                <h3 class="mt-4">Payment Receipts</h3>
                <div class="record-card-list" *ngIf="paymentReceipts.length; else noReceipts">
                    <article class="record-card finance" *ngFor="let receipt of paymentReceipts">
                        <div class="record-card-main">
                            <div class="record-card-title">
                                <span class="record-card-eyebrow">Payment receipt</span>
                                <strong>{{ receipt.paymentReference || receipt.paymentType || 'Receipt draft' }}</strong>
                                <span>{{ receipt.paymentType || 'No payment type' }}</span>
                            </div>
                            <div class="record-card-actions">
                                <p-tag [value]="receipt.status || 'PAID'" [severity]="getAssessmentSeverity(receipt.status)"></p-tag>
                                <p-button icon="pi pi-pencil" label="Edit" severity="secondary" [outlined]="true" (click)="editPaymentReceipt(receipt)"></p-button>
                            </div>
                        </div>
                        <div class="record-card-fields">
                            <div><span>Amount</span><strong>{{ formatAmount(receipt.amount, receipt.currencyCode || 'SAR') }}</strong></div>
                            <div><span>Paid party</span><strong>{{ receipt.paidParty || '-' }}</strong></div>
                            <div><span>Assessment</span><strong>{{ receipt.assessmentId || '-' }}</strong></div>
                            <div><span>Document</span><strong>{{ receipt.fileId || '-' }}</strong></div>
                            <div><span>Payment method</span><strong>{{ receipt.paymentMethod || '-' }}</strong></div>
                            <div><span>Paid at</span><strong>{{ receipt.paidAt ? (receipt.paidAt | date:'short') : '-' }}</strong></div>
                        </div>
                        <div class="record-card-note" *ngIf="receipt.remarks">{{ receipt.remarks }}</div>
                    </article>
                </div>
                <ng-template #noReceipts>
                    <div class="empty-record-state">No customs payment receipts recorded.</div>
                </ng-template>
            </div>

            <p-dialog styleClass="clearance-entry-dialog" [modal]="true" [draggable]="false" [resizable]="false"
                [visible]="activeEntryDialog === 'assessment'" (onHide)="closeEntryDialog()"
                (visibleChange)="onEntryDialogVisibleChange($event, 'assessment')"
                [header]="assessmentDraft.id ? 'Edit Duty Assessment' : 'Add Duty Assessment'">
            <div class="workspace-form dialog-form clearance-dialog-form">
                <h3>{{ assessmentDraft.id ? 'Edit Assessment' : 'Add Assessment' }}</h3>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-receipt"></i>
                        <div>
                            <h4>Assessment Reference</h4>
                            <p>Customs assessment identifier and payment context.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-hashtag"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="assessmentDraft.assessmentReferenceNo" />
                        <label>Assessment reference</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-credit-card"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="assessmentPaymentMode" [options]="customsPaymentModeOptions"
                            [(ngModel)]="assessmentDraft.paymentMode" optionLabel="label" optionValue="value"
                            appendTo="body" styleClass="w-full"></p-select>
                        <label for="assessmentPaymentMode">Payment mode</label>
                    </p-iftalabel>
                </p-inputgroup>
                    </div>
                </section>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-money-bill"></i>
                        <div>
                            <h4>Amount Breakdown</h4>
                            <p>Customs value, duty, VAT, other fees, and currency.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-calculator"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText type="number" [(ngModel)]="assessmentDraft.totalCustomsValue" />
                        <label>Total customs value</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-percentage"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText type="number" [(ngModel)]="assessmentDraft.dutyAmount" />
                        <label>Duty amount</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-receipt"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText type="number" [(ngModel)]="assessmentDraft.importVatAmount" />
                        <label>Import VAT amount</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-plus-circle"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText type="number" [(ngModel)]="assessmentDraft.otherFeesAmount" />
                        <label>Other fees amount</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-wallet"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="assessmentCurrency" [options]="currencyOptions"
                            [(ngModel)]="assessmentDraft.currencyCode" optionLabel="label" optionValue="value"
                            [filter]="true" appendTo="body" styleClass="w-full"></p-select>
                        <label for="assessmentCurrency">Currency</label>
                    </p-iftalabel>
                </p-inputgroup>
                    </div>
                </section>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-check-circle"></i>
                        <div>
                            <h4>Status And Remarks</h4>
                            <p>Payment lifecycle status and assessment notes.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-flag"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="assessmentStatus" [options]="assessmentStatusOptions"
                            [(ngModel)]="assessmentDraft.assessmentStatus" optionLabel="label" optionValue="value"
                            appendTo="body" styleClass="w-full"></p-select>
                        <label for="assessmentStatus">Assessment status</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup class="wide-field">
                    <p-inputgroup-addon><i class="pi pi-align-left"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <textarea pInputText [(ngModel)]="assessmentDraft.remarks" rows="3"></textarea>
                        <label>Assessment remarks</label>
                    </p-iftalabel>
                </p-inputgroup>
                    </div>
                </section>
                <div class="dialog-actions">
                    <p-button label="Save Assessment" icon="pi pi-save" (click)="saveAssessment()"></p-button>
                </div>
            </div>
            </p-dialog>

            <p-dialog styleClass="clearance-entry-dialog" [modal]="true" [draggable]="false" [resizable]="false"
                [visible]="activeEntryDialog === 'receipt'" (onHide)="closeEntryDialog()"
                (visibleChange)="onEntryDialogVisibleChange($event, 'receipt')"
                [header]="receiptDraft.id ? 'Edit Payment Receipt' : 'Add Payment Receipt'">
            <div class="workspace-form dialog-form clearance-dialog-form">
                <h3 class="mt-4">{{ receiptDraft.id ? 'Edit Receipt' : 'Add Payment Receipt' }}</h3>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-receipt"></i>
                        <div>
                            <h4>Payment Identity</h4>
                            <p>Receipt type, assessment reference, and payment reference.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-tag"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="receiptPaymentType" [options]="receiptPaymentTypeOptions"
                            [(ngModel)]="receiptDraft.paymentType" optionLabel="label" optionValue="value"
                            appendTo="body" styleClass="w-full"></p-select>
                        <label for="receiptPaymentType">Payment type</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-link"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="receiptAssessment" [options]="dutyTaxAssessments"
                            [(ngModel)]="receiptDraft.assessmentId" optionValue="id"
                            placeholder="Select assessment" appendTo="body" styleClass="w-full">
                            <ng-template let-assessment pTemplate="selectedItem">
                                {{ receiptAssessmentLabel(assessment) }}
                            </ng-template>
                            <ng-template let-assessment pTemplate="item">
                                {{ receiptAssessmentLabel(assessment) }}
                            </ng-template>
                        </p-select>
                        <label for="receiptAssessment">Assessment</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-hashtag"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="receiptDraft.paymentReference" />
                        <label>Payment reference</label>
                    </p-iftalabel>
                </p-inputgroup>
                    </div>
                </section>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-money-bill"></i>
                        <div>
                            <h4>Payment Amount</h4>
                            <p>Amount, currency, paid party, file evidence, and verification status.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-money-bill"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText type="number" [(ngModel)]="receiptDraft.amount" />
                        <label>Amount</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-wallet"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="receiptCurrency" [options]="currencyOptions"
                            [(ngModel)]="receiptDraft.currencyCode" optionLabel="label" optionValue="value"
                            [filter]="true" appendTo="body" styleClass="w-full"></p-select>
                        <label for="receiptCurrency">Currency</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-building"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="receiptDraft.paidParty" />
                        <label>Paid party</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-file"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="receiptDocument" [options]="receiptDocumentOptions()"
                            [(ngModel)]="receiptDraft.fileId" optionValue="fileId"
                            placeholder="Select receipt document" appendTo="body" styleClass="w-full">
                            <ng-template let-file pTemplate="selectedItem">
                                {{ receiptDocumentLabel(file) }}
                            </ng-template>
                            <ng-template let-file pTemplate="item">
                                {{ receiptDocumentLabel(file) }}
                            </ng-template>
                        </p-select>
                        <label for="receiptDocument">Receipt document</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-check-circle"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="receiptStatus" [options]="receiptStatusOptions"
                            [(ngModel)]="receiptDraft.status" optionLabel="label" optionValue="value"
                            appendTo="body" styleClass="w-full"></p-select>
                        <label for="receiptStatus">Receipt status</label>
                    </p-iftalabel>
                </p-inputgroup>
                    </div>
                </section>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-align-left"></i>
                        <div>
                            <h4>Receipt Notes</h4>
                            <p>Operational notes for finance or customs payment follow-up.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup class="wide-field">
                    <p-inputgroup-addon><i class="pi pi-align-left"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <textarea pInputText [(ngModel)]="receiptDraft.remarks" rows="3"></textarea>
                        <label>Receipt remarks</label>
                    </p-iftalabel>
                </p-inputgroup>
                    </div>
                </section>
                <div class="dialog-actions">
                    <p-button label="Save Receipt" icon="pi pi-receipt" (click)="savePaymentReceipt()"></p-button>
                </div>
            </div>
            </p-dialog>
        </div>
    </app-page-data-panel>

    <app-page-data-panel *ngIf="activeWorkspaceTab === 'containers'" title="Container Release"
        subtitle="Customs-owned release ledger for holds, inspection, line release, free time, demurrage, outgate, and container tracking links.">
        <div actions>
            <p-button label="Generate From Header" icon="pi pi-box" severity="secondary" [outlined]="true"
                (click)="generateContainersFromHeader()"></p-button>
            <p-button label="Add Container" icon="pi pi-plus" (click)="newContainer()"></p-button>
        </div>
        <div class="workspace-grid">
            <div class="record-card-list" *ngIf="customsContainers.length; else noContainers">
                <article class="record-card container" *ngFor="let container of customsContainers">
                    <div class="record-card-main">
                        <div class="record-card-title">
                            <span class="record-card-eyebrow">Container release</span>
                            <strong>{{ container.containerNo || 'No container number' }}</strong>
                            <span>{{ container.containerType || 'No container type' }}{{ container.sealNo ? ' | Seal ' + container.sealNo : '' }}</span>
                        </div>
                        <div class="record-card-actions">
                            <p-tag [value]="container.customsReleaseStatus || 'PENDING'" [severity]="getContainerSeverity(container.customsReleaseStatus)"></p-tag>
                            <p-button icon="pi pi-pencil" label="Edit" severity="secondary" [outlined]="true" (click)="editContainer(container)"></p-button>
                        </div>
                    </div>
                    <div class="record-card-fields">
                        <div><span>Line status</span><strong>{{ container.lineStatus || '-' }}</strong></div>
                        <div><span>Hold</span><strong>{{ container.holdType || 'None' }}</strong></div>
                        <div><span>Hold reason</span><strong>{{ container.holdReason || '-' }}</strong></div>
                        <div><span>Inspection</span><strong>{{ container.inspectionStatus || '-' }}</strong></div>
                        <div><span>Gross / Net</span><strong>{{ container.grossWeight || '-' }} / {{ container.netWeight || '-' }}</strong></div>
                        <div><span>Free time</span><strong>{{ container.freeTimeUntil ? (container.freeTimeUntil | date:'mediumDate') : '-' }}</strong></div>
                        <div><span>Demurrage</span><strong>{{ formatAmount(container.demurrageAmount) }}</strong></div>
                        <div><span>Detention</span><strong>{{ formatAmount(container.detentionAmount) }}</strong></div>
                        <div><span>Gate appointment</span><strong>{{ container.gateAppointmentNo || '-' }}</strong></div>
                        <div><span>Gate time</span><strong>{{ container.gateAppointmentAt ? (container.gateAppointmentAt | date:'short') : '-' }}</strong></div>
                        <div><span>Outgated</span><strong>{{ container.outgatedAt ? (container.outgatedAt | date:'short') : '-' }}</strong></div>
                        <div><span>Empty returned</span><strong>{{ container.emptyReturnedAt ? (container.emptyReturnedAt | date:'short') : '-' }}</strong></div>
                    </div>
                    <div class="record-card-footer">
                        <div class="record-card-note" *ngIf="container.remarks">{{ container.remarks }}</div>
                        <p-button *ngIf="container.containerCaseId" [label]="'Open Case #' + container.containerCaseId"
                            icon="pi pi-external-link" severity="secondary" [outlined]="true"
                            (click)="navigateToContainerCase(container.containerCaseId)"></p-button>
                    </div>
                </article>
            </div>
            <ng-template #noContainers>
                <div class="empty-record-state">No container release records. Generate from header or add one manually.</div>
            </ng-template>

            <p-dialog styleClass="clearance-entry-dialog" [modal]="true" [draggable]="false" [resizable]="false"
                [visible]="activeEntryDialog === 'container'" (onHide)="closeEntryDialog()"
                (visibleChange)="onEntryDialogVisibleChange($event, 'container')"
                [header]="containerDraft.id ? 'Edit Container Release' : 'Add Container Release'">
            <div class="workspace-form dialog-form clearance-dialog-form">
                <h3>{{ containerDraft.id ? 'Edit Container' : 'Add Container' }}</h3>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-box"></i>
                        <div>
                            <h4>Container Identity</h4>
                            <p>Container, equipment type, seal, and linked tracking case reference.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-box"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="containerDraft.containerNo" />
                        <label>Container number</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-objects-column"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="containerType" [options]="containerTypeOptions"
                            [(ngModel)]="containerDraft.containerType" optionLabel="label" optionValue="value"
                            [editable]="true" appendTo="body" styleClass="w-full"></p-select>
                        <label for="containerType">Container type</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-lock"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="containerDraft.sealNo" />
                        <label>Seal number</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup class="wide-field">
                    <p-inputgroup-addon><i class="pi pi-link"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [value]="containerDraft.containerCaseId ? ('Container case linked from clearance: ' + containerDraft.containerCaseId) : 'Container case is derived from clearance container cases'" readonly />
                        <label>Container tracking case</label>
                    </p-iftalabel>
                </p-inputgroup>
                <div class="wide-field container-case-callout" *ngIf="containerDraft.containerCaseId">
                    <i class="pi pi-link"></i>
                    <div>
                        <strong>Container tracking case is linked.</strong>
                        <span>Open the lifecycle case to manage transport, storage, events, and exceptions.</span>
                    </div>
                    <p-button [label]="'Open Case #' + containerDraft.containerCaseId" icon="pi pi-external-link"
                        severity="secondary" [outlined]="true"
                        (click)="navigateToContainerCase(containerDraft.containerCaseId)"></p-button>
                </div>
                    </div>
                </section>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-shield"></i>
                        <div>
                            <h4>Release And Holds</h4>
                            <p>Line release, customs release, hold, and inspection states.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-check-square"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="containerLineStatus" [options]="containerLineStatusOptions"
                            [(ngModel)]="containerDraft.lineStatus" optionLabel="label" optionValue="value"
                            appendTo="body" styleClass="w-full"></p-select>
                        <label for="containerLineStatus">Line status</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-flag"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="containerCustomsStatus" [options]="containerCustomsReleaseStatusOptions"
                            [(ngModel)]="containerDraft.customsReleaseStatus" optionLabel="label" optionValue="value"
                            appendTo="body" styleClass="w-full"></p-select>
                        <label for="containerCustomsStatus">Customs release status</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-ban"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="containerHoldType" [options]="containerHoldTypeOptions"
                            [(ngModel)]="containerDraft.holdType" optionLabel="label" optionValue="value"
                            appendTo="body" styleClass="w-full"></p-select>
                        <label for="containerHoldType">Hold type</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-info-circle"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="containerDraft.holdReason" />
                        <label>Hold reason</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-search"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="containerInspectionStatus" [options]="containerInspectionStatusOptions"
                            [(ngModel)]="containerDraft.inspectionStatus" optionLabel="label" optionValue="value"
                            appendTo="body" styleClass="w-full"></p-select>
                        <label for="containerInspectionStatus">Inspection status</label>
                    </p-iftalabel>
                </p-inputgroup>
                    </div>
                </section>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-calendar-clock"></i>
                        <div>
                            <h4>Dates And Charges</h4>
                            <p>Free time, demurrage, detention, gate appointment, and outgate information.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-clock"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText type="datetime-local" [(ngModel)]="containerDraft.freeTimeUntil" />
                        <label>Free time until</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-money-bill"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText type="number" [(ngModel)]="containerDraft.demurrageAmount" />
                        <label>Demurrage amount</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-wallet"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText type="number" [(ngModel)]="containerDraft.detentionAmount" />
                        <label>Detention amount</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-ticket"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="containerDraft.gateAppointmentNo" />
                        <label>Gate appointment no</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-calendar"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText type="datetime-local" [(ngModel)]="containerDraft.gateAppointmentAt" />
                        <label>Gate appointment at</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-sign-out"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText type="datetime-local" [(ngModel)]="containerDraft.outgatedAt" />
                        <label>Outgated at</label>
                    </p-iftalabel>
                </p-inputgroup>
                    </div>
                </section>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-align-left"></i>
                        <div>
                            <h4>Container Notes</h4>
                            <p>Operational remarks for release, line, or customs follow-up.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup class="wide-field">
                    <p-inputgroup-addon><i class="pi pi-align-left"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <textarea pInputText [(ngModel)]="containerDraft.remarks" rows="3"></textarea>
                        <label>Container remarks</label>
                    </p-iftalabel>
                </p-inputgroup>
                    </div>
                </section>
                <div class="dialog-actions">
                    <p-button label="Save Container" icon="pi pi-save" (click)="saveContainer()"></p-button>
                </div>
            </div>
            </p-dialog>
        </div>
    </app-page-data-panel>

    <app-page-data-panel *ngIf="activeWorkspaceTab === 'procedures'" title="GCC Advanced Procedures"
        subtitle="Track GCC transit, bonded-zone, re-export, and temporary-admission cases with guarantees and closure evidence.">
        <div actions>
            <p-button label="Add Guarantee" icon="pi pi-lock" severity="secondary" [outlined]="true"
                (click)="openEntryDialog('guarantee')" [disabled]="!selectedProcedureCase?.id"></p-button>
            <p-button label="Add Closure Event" icon="pi pi-check-circle" severity="secondary" [outlined]="true"
                (click)="openEntryDialog('closure')" [disabled]="!selectedProcedureCase?.id"></p-button>
        </div>
        <div class="workspace-provider-grid">
            <div class="workspace-tile" *ngFor="let card of procedureModeCards">
                <span>{{ card.label }}</span>
                <strong>{{ procedureCasesFor(card.mode).length }}</strong>
                <div class="text-xs text-500">{{ card.hint }}</div>
                <div class="provider-actions">
                    <p-tag [value]="procedureCasesFor(card.mode).length ? 'Tracked' : 'Not started'"
                        [severity]="procedureCasesFor(card.mode).length ? 'success' : 'warning'"></p-tag>
                    <p-button icon="pi pi-plus" severity="secondary" [text]="true" (click)="newProcedureCase(card.mode)"></p-button>
                </div>
            </div>
        </div>

        <div class="workspace-grid mt-3">
            <div>
                <h3>Procedure Cases</h3>
                <div class="record-card-list" *ngIf="procedureCases.length; else noProcedureCases">
                    <article class="record-card selectable" *ngFor="let procedureCase of procedureCases"
                        [class.selected]="selectedProcedureCase?.id === procedureCase.id" (click)="selectProcedureCase(procedureCase)">
                        <div class="record-card-main">
                            <div class="record-card-title">
                                <span class="record-card-eyebrow">Procedure case</span>
                                <strong>{{ procedureCase.procedureReferenceNo || procedureCase.procedureMode || 'Procedure case' }}</strong>
                                <span>{{ procedureCase.procedureMode || 'No procedure mode' }}</span>
                            </div>
                            <div class="record-card-actions">
                                <p-tag [value]="procedureCase.closureStatus || 'OPEN'"
                                    [severity]="procedureCase.closureStatus === 'CLOSED' ? 'success' : 'warning'"></p-tag>
                                <p-button icon="pi pi-pencil" label="Edit" severity="secondary" [outlined]="true"
                                    (click)="$event.stopPropagation(); selectProcedureCase(procedureCase); openEntryDialog('procedure')"></p-button>
                            </div>
                        </div>
                        <div class="record-card-fields">
                            <div><span>Origin / destination</span><strong>{{ procedureCase.originCountryCode || '-' }} -> {{ procedureCase.destinationCountryCode || '-' }}</strong></div>
                            <div><span>Entry port</span><strong>{{ procedureCase.entryPortName || procedureCase.entryPortCode || '-' }}</strong></div>
                            <div><span>Exit port</span><strong>{{ procedureCase.exitPortName || procedureCase.exitPortCode || '-' }}</strong></div>
                            <div><span>Warehouse</span><strong>{{ warehouseLabelById(procedureCase.bondedWarehouseId) }}</strong></div>
                            <div><span>Shipment</span><strong>{{ shipmentLabelById(procedureCase.relatedShipmentId) }}</strong></div>
                            <div><span>Declaration</span><strong>{{ declarationLabelById(procedureCase.declarationId) }}</strong></div>
                            <div><span>Expected closure</span><strong>{{ procedureCase.expectedClosureDate ? (procedureCase.expectedClosureDate | date:'mediumDate') : '-' }}</strong></div>
                            <div><span>Guarantees</span><strong>{{ guaranteesFor(procedureCase.id).length }}</strong></div>
                            <div><span>Status</span><strong>{{ procedureCase.status || '-' }}</strong></div>
                        </div>
                        <div class="record-card-footer">
                            <div class="record-card-note" *ngIf="procedureCase.remarks">{{ procedureCase.remarks }}</div>
                            <div class="record-card-actions">
                                <p-button *ngIf="procedureCase.relatedShipmentId" label="Open Shipment" icon="pi pi-truck" severity="secondary" [outlined]="true"
                                    (click)="$event.stopPropagation(); navigateToShipment(procedureCase.relatedShipmentId)"></p-button>
                                <p-button *ngIf="procedureCase.bondedWarehouseId" label="Open Warehouse" icon="pi pi-building" severity="secondary" [outlined]="true"
                                    (click)="$event.stopPropagation(); navigateToWarehouse(procedureCase.bondedWarehouseId)"></p-button>
                            </div>
                        </div>
                    </article>
                </div>
                <ng-template #noProcedureCases>
                    <div class="empty-record-state">No GCC advanced procedure cases recorded.</div>
                </ng-template>

                <h3 class="mt-4">Guarantees And Deposits</h3>
                <div class="record-card-list" *ngIf="guaranteeDeposits.length; else noGuarantees">
                    <article class="record-card finance" *ngFor="let guarantee of guaranteeDeposits">
                        <div class="record-card-main">
                            <div class="record-card-title">
                                <span class="record-card-eyebrow">Guarantee / deposit</span>
                                <strong>{{ guarantee.guaranteeReferenceNo || guarantee.guaranteeType || 'Guarantee' }}</strong>
                                <span>{{ guarantee.guaranteeType || 'No guarantee type' }}</span>
                            </div>
                            <div class="record-card-actions">
                                <p-tag [value]="guarantee.status || 'ACTIVE'" severity="info"></p-tag>
                            </div>
                        </div>
                        <div class="record-card-fields">
                            <div><span>Amount</span><strong>{{ formatAmount(guarantee.amount, guarantee.currencyCode || 'SAR') }}</strong></div>
                            <div><span>Issuing party</span><strong>{{ guarantee.issuingParty || '-' }}</strong></div>
                            <div><span>Beneficiary</span><strong>{{ guarantee.beneficiaryParty || '-' }}</strong></div>
                            <div><span>Issued at</span><strong>{{ guarantee.issuedAt ? (guarantee.issuedAt | date:'mediumDate') : '-' }}</strong></div>
                            <div><span>Expires at</span><strong>{{ guarantee.expiresAt ? (guarantee.expiresAt | date:'mediumDate') : '-' }}</strong></div>
                            <div><span>Released at</span><strong>{{ guarantee.releasedAt ? (guarantee.releasedAt | date:'mediumDate') : '-' }}</strong></div>
                            <div><span>Receipt file</span><strong>{{ guarantee.receiptFileId || '-' }}</strong></div>
                            <div><span>Evidence file</span><strong>{{ guarantee.evidenceFileId ? clearanceDocumentLabelById(guarantee.evidenceFileId) : '-' }}</strong></div>
                            <div><span>Release reference</span><strong>{{ guarantee.releaseReferenceNo || '-' }}</strong></div>
                            <div><span>Release file</span><strong>{{ guarantee.releaseFileId || '-' }}</strong></div>
                            <div><span>Declaration</span><strong>{{ declarationLabelById(guarantee.declarationId) }}</strong></div>
                        </div>
                        <div class="record-card-note" *ngIf="guarantee.remarks">{{ guarantee.remarks }}</div>
                    </article>
                </div>
                <ng-template #noGuarantees>
                    <div class="empty-record-state">No guarantees or deposits recorded.</div>
                </ng-template>

                <h3 class="mt-4">Closure Events</h3>
                <div class="record-card-list" *ngIf="closureEvents.length; else noClosureEvents">
                    <article class="record-card" *ngFor="let event of closureEvents">
                        <div class="record-card-main">
                            <div class="record-card-title">
                                <span class="record-card-eyebrow">Closure event</span>
                                <strong>{{ event.eventReferenceNo || event.eventType || 'Closure event' }}</strong>
                                <span>{{ event.eventAt ? (event.eventAt | date:'short') : 'No event time' }}</span>
                            </div>
                            <div class="record-card-actions">
                                <p-tag [value]="event.status || 'RECORDED'" severity="success"></p-tag>
                            </div>
                        </div>
                        <div class="record-card-fields">
                            <div><span>Event type</span><strong>{{ event.eventType || '-' }}</strong></div>
                            <div><span>Customs port</span><strong>{{ event.customsPortName || event.customsPortCode || '-' }}</strong></div>
                            <div><span>Evidence file</span><strong>{{ event.evidenceFileId ? clearanceDocumentLabelById(event.evidenceFileId) : '-' }}</strong></div>
                            <div><span>Declaration</span><strong>{{ declarationLabelById(event.declarationId) }}</strong></div>
                        </div>
                        <div class="record-card-note" *ngIf="event.remarks">{{ event.remarks }}</div>
                    </article>
                </div>
                <ng-template #noClosureEvents>
                    <div class="empty-record-state">No closure evidence recorded.</div>
                </ng-template>
            </div>

            <p-dialog styleClass="clearance-entry-dialog" [modal]="true" [draggable]="false" [resizable]="false"
                [visible]="activeEntryDialog === 'procedure'" (onHide)="closeEntryDialog()"
                (visibleChange)="onEntryDialogVisibleChange($event, 'procedure')"
                [header]="procedureDraft.id ? 'Edit GCC Procedure' : 'Add GCC Procedure'">
            <div class="workspace-form dialog-form clearance-dialog-form">
                <h3>{{ procedureDraft.id ? 'Edit Procedure' : 'Procedure Case' }}</h3>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-sitemap"></i>
                        <div>
                            <h4>Procedure Identity</h4>
                            <p>Advanced customs procedure mode and reference information.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-sitemap"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="procedureMode" [options]="procedureModeOptions"
                            [(ngModel)]="procedureDraft.procedureMode" optionLabel="label" optionValue="value"
                            appendTo="body" styleClass="w-full"></p-select>
                        <label for="procedureMode">Procedure mode</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-hashtag"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="procedureDraft.procedureReferenceNo" />
                        <label>Procedure reference</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-file-edit"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="procedureDeclaration" [options]="declarations"
                            [(ngModel)]="procedureDraft.declarationId" optionValue="id"
                            placeholder="Select declaration" appendTo="body" styleClass="w-full">
                            <ng-template let-declaration pTemplate="selectedItem">
                                {{ declarationOptionLabel(declaration) }}
                            </ng-template>
                            <ng-template let-declaration pTemplate="item">
                                {{ declarationOptionLabel(declaration) }}
                            </ng-template>
                        </p-select>
                        <label for="procedureDeclaration">Declaration</label>
                    </p-iftalabel>
                </p-inputgroup>
                    </div>
                </section>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-map"></i>
                        <div>
                            <h4>Route And Related Records</h4>
                            <p>Origin, destination, ports, bonded warehouse, and shipment linkage.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-flag"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="procedureOriginCountry" [options]="countries"
                            [(ngModel)]="procedureDraft.originCountryCode" optionLabel="name" optionValue="countryCode"
                            [filter]="true" [showClear]="true" appendTo="body" styleClass="w-full"></p-select>
                        <label for="procedureOriginCountry">Origin country</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-flag-fill"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="procedureDestinationCountry" [options]="countries"
                            [(ngModel)]="procedureDraft.destinationCountryCode" optionLabel="name" optionValue="countryCode"
                            [filter]="true" [showClear]="true" appendTo="body" styleClass="w-full"></p-select>
                        <label for="procedureDestinationCountry">Destination country</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-sign-in"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="procedureDraft.entryPortCode" />
                        <label>Entry port code</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-sign-out"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="procedureDraft.exitPortCode" />
                        <label>Exit port code</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-warehouse"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="procedureBondedWarehouse" [options]="warehouseOptions"
                            [(ngModel)]="procedureDraft.bondedWarehouseId" optionValue="id"
                            placeholder="Select bonded warehouse" [filter]="true" filterBy="name,code"
                            [showClear]="true" appendTo="body" styleClass="w-full">
                            <ng-template let-warehouse pTemplate="selectedItem">
                                {{ warehouseOptionLabel(warehouse) }}
                            </ng-template>
                            <ng-template let-warehouse pTemplate="item">
                                {{ warehouseOptionLabel(warehouse) }}
                            </ng-template>
                        </p-select>
                        <label for="procedureBondedWarehouse">Bonded warehouse</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-truck"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="procedureRelatedShipment" [options]="shipmentOptions"
                            [(ngModel)]="procedureDraft.relatedShipmentId" optionValue="shipmentId"
                            placeholder="Select shipment" [filter]="true" filterBy="shipmentNumber,externalReference"
                            [showClear]="true" appendTo="body" styleClass="w-full">
                            <ng-template let-shipment pTemplate="selectedItem">
                                {{ shipmentOptionLabel(shipment) }}
                            </ng-template>
                            <ng-template let-shipment pTemplate="item">
                                {{ shipmentOptionLabel(shipment) }}
                            </ng-template>
                        </p-select>
                        <label for="procedureRelatedShipment">Related shipment</label>
                    </p-iftalabel>
                </p-inputgroup>
                    </div>
                </section>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-check-circle"></i>
                        <div>
                            <h4>Closure Readiness</h4>
                            <p>Expected closure date, procedure status, closure status, and remarks.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-calendar"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText type="datetime-local" [(ngModel)]="procedureDraft.expectedClosureDate" />
                        <label>Expected closure</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-flag"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="procedureStatus" [options]="procedureStatusOptions"
                            [(ngModel)]="procedureDraft.status" optionLabel="label" optionValue="value"
                            appendTo="body" styleClass="w-full"></p-select>
                        <label for="procedureStatus">Procedure status</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-check"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="procedureClosureStatus" [options]="procedureStatusOptions"
                            [(ngModel)]="procedureDraft.closureStatus" optionLabel="label" optionValue="value"
                            appendTo="body" styleClass="w-full"></p-select>
                        <label for="procedureClosureStatus">Closure status</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup class="wide-field">
                    <p-inputgroup-addon><i class="pi pi-align-left"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <textarea pInputText [(ngModel)]="procedureDraft.remarks" rows="3"></textarea>
                        <label>Procedure remarks</label>
                    </p-iftalabel>
                </p-inputgroup>
                    </div>
                </section>
                <div class="dialog-actions">
                    <p-button label="Save Procedure" icon="pi pi-save" (click)="saveProcedureCase()"></p-button>
                </div>
            </div>
            </p-dialog>

            <p-dialog styleClass="clearance-entry-dialog" [modal]="true" [draggable]="false" [resizable]="false"
                [visible]="activeEntryDialog === 'guarantee'" (onHide)="closeEntryDialog()"
                (visibleChange)="onEntryDialogVisibleChange($event, 'guarantee')"
                header="Add Guarantee / Deposit">
            <div class="workspace-form dialog-form clearance-dialog-form">
                <h3 class="mt-4">Guarantee / Deposit</h3>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-lock"></i>
                        <div>
                            <h4>Guarantee Terms</h4>
                            <p>Guarantee type, reference, value, expiry, and lifecycle status.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-lock"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="guaranteeType" [options]="guaranteeTypeOptions"
                            [(ngModel)]="guaranteeDraft.guaranteeType" optionLabel="label" optionValue="value"
                            appendTo="body" styleClass="w-full"></p-select>
                        <label for="guaranteeType">Guarantee type</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-hashtag"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="guaranteeDraft.guaranteeReferenceNo" />
                        <label>Guarantee reference</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-money-bill"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText type="number" [(ngModel)]="guaranteeDraft.amount" />
                        <label>Amount</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-wallet"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="guaranteeCurrency" [options]="currencyOptions"
                            [(ngModel)]="guaranteeDraft.currencyCode" optionLabel="label" optionValue="value"
                            [filter]="true" appendTo="body" styleClass="w-full"></p-select>
                        <label for="guaranteeCurrency">Currency</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-calendar"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText type="datetime-local" [(ngModel)]="guaranteeDraft.expiresAt" />
                        <label>Expiry</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-flag"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="guaranteeStatus" [options]="guaranteeStatusOptions"
                            [(ngModel)]="guaranteeDraft.status" optionLabel="label" optionValue="value"
                            appendTo="body" styleClass="w-full"></p-select>
                        <label for="guaranteeStatus">Status</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-file"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="guaranteeEvidenceFile" [options]="clearanceDocumentOptions()"
                            [(ngModel)]="guaranteeDraft.evidenceFileId" optionValue="fileId"
                            placeholder="Select evidence document" [filter]="true" filterBy="fileName,fileType"
                            [showClear]="true" appendTo="body" styleClass="w-full">
                            <ng-template let-file pTemplate="selectedItem">
                                {{ clearanceDocumentLabel(file) }}
                            </ng-template>
                            <ng-template let-file pTemplate="item">
                                {{ clearanceDocumentLabel(file) }}
                            </ng-template>
                        </p-select>
                        <label for="guaranteeEvidenceFile">Evidence document</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-file-edit"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="guaranteeDeclaration" [options]="declarations"
                            [(ngModel)]="guaranteeDraft.declarationId" optionValue="id"
                            placeholder="Select declaration" appendTo="body" styleClass="w-full">
                            <ng-template let-declaration pTemplate="selectedItem">
                                {{ declarationOptionLabel(declaration) }}
                            </ng-template>
                            <ng-template let-declaration pTemplate="item">
                                {{ declarationOptionLabel(declaration) }}
                            </ng-template>
                        </p-select>
                        <label for="guaranteeDeclaration">Declaration</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup class="wide-field">
                    <p-inputgroup-addon><i class="pi pi-align-left"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <textarea pInputText [(ngModel)]="guaranteeDraft.remarks" rows="2"></textarea>
                        <label>Guarantee remarks</label>
                    </p-iftalabel>
                </p-inputgroup>
                    </div>
                </section>
                <div class="dialog-actions">
                    <p-button label="Save Guarantee" icon="pi pi-lock" severity="secondary" [outlined]="true"
                        (click)="saveGuaranteeDeposit()"></p-button>
                </div>
            </div>
            </p-dialog>

            <p-dialog styleClass="clearance-entry-dialog" [modal]="true" [draggable]="false" [resizable]="false"
                [visible]="activeEntryDialog === 'closure'" (onHide)="closeEntryDialog()"
                (visibleChange)="onEntryDialogVisibleChange($event, 'closure')"
                header="Add Closure Event">
            <div class="workspace-form dialog-form clearance-dialog-form">
                <h3 class="mt-4">Closure Event</h3>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-check-circle"></i>
                        <div>
                            <h4>Closure Evidence</h4>
                            <p>Closure event reference, time, port, evidence file, and remarks.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-tag"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="closureEventType" [options]="closureEventTypeOptions"
                            [(ngModel)]="closureEventDraft.eventType" optionLabel="label" optionValue="value"
                            appendTo="body" styleClass="w-full"></p-select>
                        <label for="closureEventType">Closure event type</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-hashtag"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="closureEventDraft.eventReferenceNo" />
                        <label>Closure reference</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-calendar-clock"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText type="datetime-local" [(ngModel)]="closureEventDraft.eventAt" />
                        <label>Event time</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-map-marker"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="closureEventDraft.customsPortCode" />
                        <label>Customs port code</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-file"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="closureEvidenceFile" [options]="clearanceDocumentOptions()"
                            [(ngModel)]="closureEventDraft.evidenceFileId" optionValue="fileId"
                            placeholder="Select evidence document" [filter]="true" filterBy="fileName,fileType"
                            [showClear]="true" appendTo="body" styleClass="w-full">
                            <ng-template let-file pTemplate="selectedItem">
                                {{ clearanceDocumentLabel(file) }}
                            </ng-template>
                            <ng-template let-file pTemplate="item">
                                {{ clearanceDocumentLabel(file) }}
                            </ng-template>
                        </p-select>
                        <label for="closureEvidenceFile">Evidence document</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-file-edit"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="closureDeclaration" [options]="declarations"
                            [(ngModel)]="closureEventDraft.declarationId" optionValue="id"
                            placeholder="Select declaration" appendTo="body" styleClass="w-full">
                            <ng-template let-declaration pTemplate="selectedItem">
                                {{ declarationOptionLabel(declaration) }}
                            </ng-template>
                            <ng-template let-declaration pTemplate="item">
                                {{ declarationOptionLabel(declaration) }}
                            </ng-template>
                        </p-select>
                        <label for="closureDeclaration">Declaration</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-check-circle"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="closureEventStatus" [options]="closureStatusOptions"
                            [(ngModel)]="closureEventDraft.status" optionLabel="label" optionValue="value"
                            appendTo="body" styleClass="w-full"></p-select>
                        <label for="closureEventStatus">Closure status</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup class="wide-field">
                    <p-inputgroup-addon><i class="pi pi-align-left"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <textarea pInputText [(ngModel)]="closureEventDraft.remarks" rows="2"></textarea>
                        <label>Closure remarks</label>
                    </p-iftalabel>
                </p-inputgroup>
                    </div>
                </section>
                <div class="dialog-actions">
                    <p-button label="Save Closure" icon="pi pi-check-circle" severity="secondary" [outlined]="true"
                        (click)="saveClosureEvent()"></p-button>
                </div>
            </div>
            </p-dialog>
        </div>
    </app-page-data-panel>

    <app-page-data-panel *ngIf="activeWorkspaceTab === 'integrations'" title="KSA Integration Status"
        subtitle="Manual external references and sync history for Fasah, Bayan, ZATCA Tariff, and Saber.">
        <div class="workspace-provider-grid">
            <div class="workspace-tile" *ngFor="let provider of integrationProviders">
                <span>{{ provider.label }}</span>
                <strong>{{ getIntegrationStatus(provider.key)?.externalStatus || 'Not configured' }}</strong>
                <div class="text-xs text-500">{{ getIntegrationStatus(provider.key)?.externalReference || 'No external reference' }}</div>
                <div class="provider-actions">
                    <p-tag [value]="getIntegrationStatus(provider.key)?.internalStatus || 'MISSING'"
                        [severity]="getIntegrationSeverity(getIntegrationStatus(provider.key))"></p-tag>
                    <p-button icon="pi pi-plus" severity="secondary" [text]="true" (click)="newIntegrationStatus(provider.key)"></p-button>
                </div>
            </div>
        </div>

        <div class="grid mt-3">
            <div class="col-12 md:col-6">
                <h3>Warnings</h3>
                <div *ngIf="!integrationSummary?.warnings?.length" class="empty-note">No integration warnings.</div>
                <div *ngFor="let warning of integrationSummary?.warnings" class="issue-row warn">{{ warning }}</div>
            </div>
            <div class="col-12 md:col-6">
                <h3>Errors</h3>
                <div *ngIf="!integrationSummary?.errors?.length" class="empty-note">No integration errors.</div>
                <div *ngFor="let error of integrationSummary?.errors" class="issue-row danger">{{ error }}</div>
            </div>
        </div>

        <div class="workspace-grid mt-3">
            <div>
                <h3>External Statuses</h3>
                <div class="record-card-list" *ngIf="integrationStatuses.length; else noIntegrationStatuses">
                    <article class="record-card integration" *ngFor="let status of integrationStatuses">
                        <div class="record-card-main">
                            <div class="record-card-title">
                                <span class="record-card-eyebrow">External status</span>
                                <strong>{{ status.provider || 'Provider' }}</strong>
                                <span>{{ status.externalReference || 'No external reference' }}</span>
                            </div>
                            <div class="record-card-actions">
                                <p-tag [value]="status.externalStatus || status.internalStatus || 'PENDING'" [severity]="getIntegrationSeverity(status)"></p-tag>
                                <p-button icon="pi pi-refresh" label="Refresh" severity="secondary" [outlined]="true" (click)="refreshIntegrationStatus(status)"></p-button>
                                <p-button icon="pi pi-pencil" label="Edit" severity="secondary" [outlined]="true" (click)="editIntegrationStatus(status)"></p-button>
                            </div>
                        </div>
                        <div class="record-card-fields">
                            <div><span>Internal status</span><strong>{{ status.internalStatus || '-' }}</strong></div>
                            <div><span>Declaration</span><strong>{{ declarationLabelById(status.declarationId) }}</strong></div>
                            <div><span>Last request</span><strong>{{ status.lastRequestAt ? (status.lastRequestAt | date:'short') : '-' }}</strong></div>
                            <div><span>Last response</span><strong>{{ status.lastResponseAt ? (status.lastResponseAt | date:'short') : '-' }}</strong></div>
                            <div><span>Last sync</span><strong>{{ status.lastSyncedAt ? (status.lastSyncedAt | date:'short') : '-' }}</strong></div>
                            <div><span>Next retry</span><strong>{{ status.nextRetryAt ? (status.nextRetryAt | date:'short') : '-' }}</strong></div>
                            <div><span>Retries</span><strong>{{ status.retryCount || 0 }}</strong></div>
                            <div><span>Active</span><strong>{{ status.active ? 'Yes' : 'No' }}</strong></div>
                            <div><span>Created by</span><strong>{{ status.createdByActor?.name || status.createdBy || '-' }}</strong></div>
                        </div>
                        <div class="record-card-note danger" *ngIf="status.errorMessage || status.errorCode">
                            {{ status.errorMessage || status.errorCode }}
                        </div>
                    </article>
                </div>
                <ng-template #noIntegrationStatuses>
                    <div class="empty-record-state">No integration statuses recorded yet.</div>
                </ng-template>

                <h3 class="mt-4">Sync And Update History</h3>
                <div class="record-card-list compact" *ngIf="integrationEvents.length; else noIntegrationEvents">
                    <article class="record-card" *ngFor="let event of integrationEvents">
                        <div class="record-card-main">
                            <div class="record-card-title">
                                <span class="record-card-eyebrow">Integration event</span>
                                <strong>{{ event.actionType || 'Provider update' }}</strong>
                                <span>{{ event.createdAt | date:'short' }}</span>
                            </div>
                            <div class="record-card-actions">
                                <p-tag [value]="event.resultStatus || '-'" [severity]="event.errorMessage || event.errorCode ? 'danger' : 'success'"></p-tag>
                            </div>
                        </div>
                        <div class="record-card-fields">
                            <div><span>Provider</span><strong>{{ event.provider || '-' }}</strong></div>
                            <div><span>Declaration</span><strong>{{ declarationLabelById(event.declarationId) }}</strong></div>
                            <div><span>Status change</span><strong>{{ event.externalStatusBefore || '-' }} -> {{ event.externalStatusAfter || '-' }}</strong></div>
                            <div><span>Actor</span><strong>{{ event.actor?.name || event.createdBy || 'System' }}</strong></div>
                            <div><span>Payload</span><strong>{{ event.payloadLocation || '-' }}</strong></div>
                        </div>
                        <div class="record-card-note danger" *ngIf="event.errorMessage || event.errorCode">
                            {{ event.errorMessage || event.errorCode }}
                        </div>
                    </article>
                </div>
                <ng-template #noIntegrationEvents>
                    <div class="empty-record-state">No integration history recorded.</div>
                </ng-template>
            </div>

            <p-dialog styleClass="clearance-entry-dialog" [modal]="true" [draggable]="false" [resizable]="false"
                [visible]="activeEntryDialog === 'integration'" (onHide)="closeEntryDialog()"
                (visibleChange)="onEntryDialogVisibleChange($event, 'integration')"
                [header]="integrationDraft.id ? 'Edit Integration Status' : 'Add Integration Status'">
            <div class="workspace-form dialog-form clearance-dialog-form">
                <h3>{{ integrationDraft.id ? 'Edit Integration Status' : 'Add Integration Status' }}</h3>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-cloud"></i>
                        <div>
                            <h4>Provider Link</h4>
                            <p>External provider, declaration, reference, and mapped statuses.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-cloud"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="integrationProvider" [options]="integrationProviders"
                            [(ngModel)]="integrationDraft.provider" optionLabel="label" optionValue="key"
                            appendTo="body" styleClass="w-full"></p-select>
                        <label for="integrationProvider">Provider</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-file-edit"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="integrationDeclaration" [options]="declarations"
                            [(ngModel)]="integrationDraft.declarationId" optionValue="id"
                            placeholder="Select declaration" appendTo="body" styleClass="w-full">
                            <ng-template let-declaration pTemplate="selectedItem">
                                {{ declarationOptionLabel(declaration) }}
                            </ng-template>
                            <ng-template let-declaration pTemplate="item">
                                {{ declarationOptionLabel(declaration) }}
                            </ng-template>
                        </p-select>
                        <label for="integrationDeclaration">Declaration</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-hashtag"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="integrationDraft.externalReference" />
                        <label>External reference</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-globe"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="integrationExternalStatus" [options]="customsStatusOptions"
                            [(ngModel)]="integrationDraft.externalStatus" optionLabel="label" optionValue="value"
                            [editable]="true" appendTo="body" styleClass="w-full"></p-select>
                        <label for="integrationExternalStatus">External status</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-check-circle"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="integrationInternalStatus" [options]="integrationInternalStatusOptions"
                            [(ngModel)]="integrationDraft.internalStatus" optionLabel="label" optionValue="value"
                            appendTo="body" styleClass="w-full"></p-select>
                        <label for="integrationInternalStatus">Internal status</label>
                    </p-iftalabel>
                </p-inputgroup>
                    </div>
                </section>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-exclamation-triangle"></i>
                        <div>
                            <h4>Error And Retry Handling</h4>
                            <p>Provider error details, payload storage location, retry timing, and active state.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-times-circle"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="integrationDraft.errorCode" />
                        <label>Error code</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup class="wide-field">
                    <p-inputgroup-addon><i class="pi pi-align-left"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <textarea pInputText [(ngModel)]="integrationDraft.errorMessage" rows="3"></textarea>
                        <label>Error message</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-database"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="integrationDraft.rawStatusPayloadLocation" />
                        <label>Payload location</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-clock"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText type="datetime-local" [(ngModel)]="integrationDraft.nextRetryAt" />
                        <label>Next retry at</label>
                    </p-iftalabel>
                </p-inputgroup>
                <label><input type="checkbox" [(ngModel)]="integrationDraft.active" /> Active</label>
                    </div>
                </section>
                <div class="dialog-actions">
                    <p-button label="Save Status" icon="pi pi-save" (click)="saveIntegrationStatus()"></p-button>
                    <p-button label="Manual Status Update" icon="pi pi-history" severity="secondary" [outlined]="true"
                        (click)="manualIntegrationStatusUpdate()"></p-button>
                </div>
            </div>
            </p-dialog>
        </div>
    </app-page-data-panel>

    <app-page-data-panel *ngIf="activeWorkspaceTab === 'links'" title="Typed Module Links"
        subtitle="Derived links from shipment, container cases, warehousing procedures, and accounting records.">
        <div class="workspace-provider-grid">
            <div class="workspace-tile">
                <span>Shipment</span>
                <strong>{{ shipmentReference }}</strong>
                <div class="text-xs text-500">{{ moduleLinks?.shipmentRoute || 'Derived from the clearance header and module links' }}</div>
                <p-button label="Open" icon="pi pi-external-link" [disabled]="!linkedShipmentId"
                    (click)="navigateToShipment()"></p-button>
            </div>
            <div class="workspace-tile">
                <span>Container Tracking</span>
                <strong>{{ linkedContainerCaseIds.length }}</strong>
                <div class="text-xs text-500">Derived from container release rows created for this clearance</div>
                <div class="provider-actions" *ngFor="let container of customsContainers">
                    <p-tag [value]="container.containerNo || 'Container'" severity="info"></p-tag>
                    <p-button *ngIf="container.containerCaseId" icon="pi pi-box" severity="secondary" [text]="true"
                        (click)="navigateToContainerCase(container.containerCaseId)"></p-button>
                </div>
            </div>
            <div class="workspace-tile">
                <span>Warehousing</span>
                <strong>{{ linkedWarehouses.length }}</strong>
                <div class="text-xs text-500">From bonded procedure cases</div>
                <div class="provider-actions" *ngFor="let warehouseId of linkedWarehouses">
                    <p-tag value="Bonded warehouse" severity="info"></p-tag>
                    <p-button icon="pi pi-external-link" severity="secondary" [text]="true"
                        (click)="navigateToWarehouse(warehouseId)"></p-button>
                </div>
            </div>
            <div class="workspace-tile">
                <span>Accounting</span>
                <strong>{{ accountingLinks.length }}</strong>
                <div class="text-xs text-500">Receivables, invoices, payables, and bills</div>
                <p-button label="Generate Payables" icon="pi pi-wallet" [outlined]="true" (click)="generatePayables()"></p-button>
                <div class="provider-actions" *ngFor="let link of accountingLinks">
                    <p-tag [value]="link.invoiceNumber || link.receivableReferenceNumber || link.billNumber || link.payableReferenceNumber || 'Finance record'" severity="success"></p-tag>
                    <p-button icon="pi pi-external-link" severity="secondary" [text]="true"
                        (click)="navigateToAccounting(link)"></p-button>
                </div>
            </div>
        </div>

        <h3 class="mt-4">All Typed Links</h3>
        <div class="record-card-list" *ngIf="(moduleLinks?.links || []).length; else noTypedLinks">
            <article class="record-card link" *ngFor="let link of moduleLinks?.links || []">
                <div class="record-card-main">
                    <div class="record-card-title">
                        <span class="record-card-eyebrow">{{ link.moduleKey || 'Module link' }}</span>
                        <strong>{{ link.referenceNumber || link.label || 'Linked record' }}</strong>
                        <span>{{ link.label || '-' }}</span>
                    </div>
                    <div class="record-card-actions">
                        <p-tag [value]="link.status || '-'" severity="info"></p-tag>
                        <p-button icon="pi pi-external-link" label="Open" severity="secondary" [outlined]="true" (click)="navigateToTypedLink(link)"></p-button>
                    </div>
                </div>
                <div class="record-card-fields">
                    <div><span>Relation</span><strong>{{ link.relationType || '-' }}</strong></div>
                    <div><span>Route</span><strong>{{ link.route || '-' }}</strong></div>
                    <div><span>Reference</span><strong>{{ link.referenceNumber || '-' }}</strong></div>
                    <div><span>Label</span><strong>{{ link.label || '-' }}</strong></div>
                </div>
            </article>
        </div>
        <ng-template #noTypedLinks>
            <div class="empty-record-state">No typed module links are available.</div>
        </ng-template>

    </app-page-data-panel>

    <app-page-data-panel *ngIf="activeWorkspaceTab === 'workflow'" title="Workflow"
        subtitle="Configurable clearance workflow using resourceKey CUSTOMS_CLEARANCE.">
        <app-workflow-panel
            resourceKey="CUSTOMS_CLEARANCE"
            [resourceId]="clearanceId"
            [companyId]="clearance.owningCompanyId"
            [context]="workflowContext">
        </app-workflow-panel>
    </app-page-data-panel>

    <app-page-data-panel *ngIf="activeWorkspaceTab === 'productivity'" title="Productivity"
        subtitle="Notifications, tasks, and incidents linked with resourceKey CUSTOMS_CLEARANCE.">
        <div actions>
            <p-button label="Create Task" icon="pi pi-check-square" (click)="newTask()"></p-button>
        </div>
        <div class="mb-3">
            <p-button label="Sync Productivity" icon="pi pi-refresh" [outlined]="true" (click)="syncProductivity()"></p-button>
            <span class="text-sm text-500 ml-2">{{ productivityNotifications.length }} notifications, {{ moduleLinks?.openTaskCount || productivityTasks.length }} open tasks, {{ moduleLinks?.openIncidentCount || productivityIncidents.length }} open incidents</span>
        </div>
        <div class="workspace-grid">
            <div>
                <h3>Notifications</h3>
                <div class="record-card-list compact" *ngIf="productivityNotifications.length; else noNotifications">
                    <article class="record-card productivity" *ngFor="let notification of productivityNotifications">
                        <div class="record-card-main">
                            <div class="record-card-title">
                                <span class="record-card-eyebrow">Notification</span>
                                <strong>{{ notification.title }}</strong>
                                <span>{{ notification.message || 'No message' }}</span>
                            </div>
                            <div class="record-card-actions">
                                <p-tag [value]="notification.severity || '-'" severity="info"></p-tag>
                            </div>
                        </div>
                        <div class="record-card-fields">
                            <div><span>Created</span><strong>{{ notification.createdAt ? (notification.createdAt | date:'short') : '-' }}</strong></div>
                            <div><span>Read</span><strong>{{ notification.readFlag ? 'Yes' : 'No' }}</strong></div>
                            <div><span>Resource</span><strong>{{ notification.resourceKey || '-' }}</strong></div>
                            <div><span>Action URL</span><strong>{{ notification.actionUrl || '-' }}</strong></div>
                        </div>
                    </article>
                </div>
                <ng-template #noNotifications>
                    <div class="empty-record-state">No clearance notifications.</div>
                </ng-template>
                <h3>Tasks</h3>
                <div class="record-card-list compact" *ngIf="productivityTasks.length; else noTasks">
                    <article class="record-card productivity" *ngFor="let task of productivityTasks">
                        <div class="record-card-main">
                            <div class="record-card-title">
                                <span class="record-card-eyebrow">Task</span>
                                <strong>{{ task.title }}</strong>
                                <span>{{ task.description || 'No description' }}</span>
                            </div>
                            <div class="record-card-actions">
                                <p-tag [value]="task.status || '-'" severity="info"></p-tag>
                            </div>
                        </div>
                        <div class="record-card-fields">
                            <div><span>Priority</span><strong>{{ task.priority || '-' }}</strong></div>
                            <div><span>Assigned to</span><strong>{{ task.assignedCustomer?.name || task.assignedCustomerId || '-' }}</strong></div>
                            <div><span>Due date</span><strong>{{ task.dueDate ? (task.dueDate | date:'short') : '-' }}</strong></div>
                            <div><span>Created</span><strong>{{ task.createdAt ? (task.createdAt | date:'short') : '-' }}</strong></div>
                            <div><span>Resource</span><strong>{{ task.resourceKey || '-' }}</strong></div>
                            <div><span>Completion notes</span><strong>{{ task.completionNotes || '-' }}</strong></div>
                        </div>
                    </article>
                </div>
                <ng-template #noTasks>
                    <div class="empty-record-state">No clearance tasks.</div>
                </ng-template>
                <h3 class="mt-4">Incidents</h3>
                <div class="record-card-list compact" *ngIf="productivityIncidents.length; else noIncidents">
                    <article class="record-card productivity" *ngFor="let incident of productivityIncidents">
                        <div class="record-card-main">
                            <div class="record-card-title">
                                <span class="record-card-eyebrow">Incident</span>
                                <strong>{{ incident.title }}</strong>
                                <span>{{ incident.description || 'No description' }}</span>
                            </div>
                            <div class="record-card-actions">
                                <p-tag [value]="incident.status || '-'" [severity]="incident.status === 'RESOLVED' ? 'success' : 'warning'"></p-tag>
                            </div>
                        </div>
                        <div class="record-card-fields">
                            <div><span>Category</span><strong>{{ incident.category || '-' }}</strong></div>
                            <div><span>Severity</span><strong>{{ incident.severity || '-' }}</strong></div>
                            <div><span>Reporter</span><strong>{{ incident.reporterCustomer?.name || incident.reporterCustomerId || '-' }}</strong></div>
                            <div><span>Owner</span><strong>{{ incident.ownerCustomer?.name || incident.ownerCustomerId || '-' }}</strong></div>
                            <div><span>Created</span><strong>{{ incident.createdAt ? (incident.createdAt | date:'short') : '-' }}</strong></div>
                            <div><span>Resolved</span><strong>{{ incident.resolvedAt ? (incident.resolvedAt | date:'short') : '-' }}</strong></div>
                        </div>
                        <div class="record-card-note" *ngIf="incident.resolutionNotes">{{ incident.resolutionNotes }}</div>
                    </article>
                </div>
                <ng-template #noIncidents>
                    <div class="empty-record-state">No clearance incidents.</div>
                </ng-template>
            </div>
            <p-dialog styleClass="clearance-entry-dialog" [modal]="true" [draggable]="false" [resizable]="false"
                [visible]="activeEntryDialog === 'task'" (onHide)="closeEntryDialog()"
                (visibleChange)="onEntryDialogVisibleChange($event, 'task')"
                header="Create Productivity Task">
            <div class="workspace-form dialog-form clearance-dialog-form">
                <h3>Create Task</h3>
                <section class="dialog-section">
                    <div class="dialog-section-heading">
                        <i class="pi pi-check-square"></i>
                        <div>
                            <h4>Task Details</h4>
                            <p>Work item title, description, and priority for clearance productivity.</p>
                        </div>
                    </div>
                    <div class="dialog-section-grid">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-check-square"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input pInputText [(ngModel)]="taskDraft.title" />
                        <label>Task title</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup class="wide-field">
                    <p-inputgroup-addon><i class="pi pi-align-left"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <textarea pInputText [(ngModel)]="taskDraft.description" rows="3"></textarea>
                        <label>Description</label>
                    </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-flag"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="taskPriority" [options]="taskPriorityOptions"
                            [(ngModel)]="taskDraft.priority" optionLabel="label" optionValue="value"
                            appendTo="body" styleClass="w-full"></p-select>
                        <label for="taskPriority">Priority</label>
                    </p-iftalabel>
                </p-inputgroup>
                    </div>
                </section>
                <div class="dialog-actions">
                    <p-button label="Create Task" icon="pi pi-check-square" (click)="createTask()"></p-button>
                </div>
            </div>
            </p-dialog>
        </div>
    </app-page-data-panel>

    <app-page-data-panel *ngIf="activeWorkspaceTab === 'activity'" title="Activity Log"
        subtitle="Persisted audit events from declaration, documents, authorization, links, and readiness actions.">
        <div class="record-card-list compact" *ngIf="activity.length; else noActivity">
            <article class="record-card activity" *ngFor="let event of activity">
                <div class="record-card-main">
                    <div class="record-card-title">
                        <span class="record-card-eyebrow">Activity</span>
                        <strong>{{ event.eventType || 'Event' }}</strong>
                        <span>{{ event.createdAt | date:'short' }}</span>
                    </div>
                    <div class="record-card-actions">
                        <p-tag [value]="event.entityType || '-'" severity="info"></p-tag>
                    </div>
                </div>
                <div class="record-card-fields">
                    <div><span>Entity ID</span><strong>{{ event.entityId || '-' }}</strong></div>
                    <div><span>Source</span><strong>{{ event.eventSource || '-' }}</strong></div>
                    <div><span>External reference</span><strong>{{ event.externalReference || '-' }}</strong></div>
                    <div><span>Created by</span><strong>{{ event.actor?.name || event.createdBy || 'System' }}</strong></div>
                    <div><span>Old value</span><strong>{{ event.oldValue || '-' }}</strong></div>
                    <div><span>New value</span><strong>{{ event.newValue || '-' }}</strong></div>
                </div>
                <div class="record-card-note" *ngIf="event.message">{{ event.message }}</div>
            </article>
        </div>
        <ng-template #noActivity>
            <div class="empty-record-state">No activity events recorded.</div>
        </ng-template>
    </app-page-data-panel>
    <app-field-resources-panel
        *ngIf="clearance?.clearanceHeaderID"
        targetType="CUSTOMS_CLEARANCE_HEADER"
        [targetId]="clearance.clearanceHeaderID"
        [targetReference]="clearance.jobNumber || clearance.clearanceJobNumber || clearance.bl_awb_number"
        [logisticsProjectId]="clearance.logisticsProjectId"
        [customerCrmClientId]="clearance.crmClientId">
    </app-field-resources-panel>
</app-page-shell>

<div *ngIf="!clearance && !isLoading" class="card p-4 text-center text-500">
    Clearance not found or an error occurred.
</div>

<app-add-edit-clearance-charges [(visible)]="chargeDialogVisible" [clearance]="clearance"
    [clearanceCharge]="selectedCharge" (chargeSaved)="onChargeSaved($event)">
</app-add-edit-clearance-charges>
